/**
 * 把純 reducer（machine.ts）接到真實世界：REST + WebSocket + 計時器。
 * 所有「決定畫面該長怎樣」的邏輯都在 machine.ts；這裡只負責搬運副作用。
 */

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { ApiError, randomIdempotencyKey } from '../api/client';
import type { ActionKey, RematchResponse, WsServerMessage } from '../api/types';
import { useApp } from '../app/AppContext';
import { config } from '../config';
import { initialState, matchReducer, PROTOCOL_MISMATCH, type MatchMachineState } from './machine';

/** 揭曉動畫時間，必須跟 --dur-reveal 對得上。 */
const REVEAL_MS = 900;

export interface UseMatchResult {
  state: MatchMachineState;
  /**
   * ★ 再戰產生的新對局 id（C02 §5 v1.2 的 `rematch_ready`）。null = 目前沒有。
   * 由頁面層負責導向 —— 這裡只負責把它讀出來。
   */
  rematchMatchId: string | null;
  /** 距離 deadline 還有幾毫秒（已做伺服器時鐘校正）。null = 沒有進行中的 deadline。 */
  remainingMs: number | null;
  /** 0..1，給倒數環用。 */
  progress: number;
  submit(action: ActionKey): void;
  rematch(): Promise<RematchResponse>;
  /**
   * ★ **對再戰意願零副作用**的「我是不是已經被放進另一場對局了？」查詢。
   *
   * 回傳 `GET /lobby/{game_key}` 的 `my_active_match`，但**只在它不是目前這場**時
   * 才回傳（同一場沒有任何意義）。null = 沒有別的進行中對局。
   *
   * 存在的理由見 `MatchPage` 的 `REMATCH_WATCH_INTERVAL_MS`：再戰的尾窗裡
   * 不能再打 `POST /rematch`（那會續期，鑰匙就永遠不死），但又不能停止尋找 ——
   * 對手這時按下再戰，後端會真的把我放進一個座位，而 `rematch_ready` 是會掉的。
   * 這條 GET 讀的是 `active_match_for_subject()`（status ∈ waiting/ready/in_progress，
   * 取 `match_id` 最大的一筆；ULID 單調遞增 ⇒ 就是最新開的那一場），
   * **完全不碰 redis 的再戰 key**。
   */
  findMyActiveMatch(): Promise<string | null>;
  forfeit(): Promise<void>;
  refresh(): void;
  loadError: string | null;
}

/**
 * ★ 讀取 reducer（machine.ts）在收到 `rematch_ready` 時填進 state 的再戰目標。
 *
 * 介面由 orchestrator 釘死：`MatchMachineState.rematchMatchId: string | null`，
 * **reducer 那一側只負責填、這一側只負責讀**，兩邊都不跨界。
 *
 * 讀的時候**再檢查一次**「非空字串」，與契約對 reducer 的要求是同一條規則：
 * 一個沒有 id 的再戰事件是壞掉的訊號，不是可以猜的東西，寧可當作沒有。
 * 導頁是不可逆的（玩家會離開這個畫面），所以這一步不省。
 */
export function readRematchMatchId(state: MatchMachineState): string | null {
  const v: unknown = state.rematchMatchId;
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/**
 * ★ 把 `POST /rematch` 的失敗歸類成一個**一定存在**的錯誤碼。
 *
 * 為什麼要有這支：`MatchPage` 的 `catch` 原本只做 `setRematchSent(false)` ——
 * 409 `MATCH_NOT_ENDED` 因此被完全吞掉，玩家看到按鈕彈回可按、畫面一個字都沒有，
 * 於是他會以為自己點歪了而反覆按。**要顯示訊息，就得先有一個可以對照的碼。**
 *
 * `ApiError` 一定帶 `code`（伺服器的 `error.code`，或 `HTTP_<status>`）；
 * 其他任何東西（fetch 直接爆掉、程式錯誤）一律歸成 `NETWORK_ERROR`，
 * 與 `ApiError.network()` 用的是同一個字串 —— 兩條路徑不得產生兩套詞彙。
 */
export function rematchErrorCode(e: unknown): string {
  return e instanceof ApiError ? e.code : 'NETWORK_ERROR';
}

/**
 * ★ `GET /lobby/{game_key}` 的 `my_active_match` → 「要不要把玩家帶過去」。
 *
 * 抽成純函式的理由跟 `readRematchMatchId()` 一模一樣：這是一條**新的跨服務接縫**
 * （後端 `lobby/service.py:144` 產生 → 這裡消費），而本專案已經被同一種接縫咬過四次。
 * 抽出來就測得到，測得到就不會靠「兩邊剛好都對」。
 *
 * 防禦與判斷：
 *  * **不是非空字串就當作沒有** —— 導頁是不可逆的，寧可少導一次。
 *  * **等於目前這場就當作沒有** —— 玩家正坐在一場已結束的對局上，
 *    後端不會把它算成 active（status 不在 waiting/ready/in_progress）；
 *    這道 guard 是為了讓「後端哪天改了語意」的最壞情況是「什麼都沒發生」，
 *    而不是把玩家導向他自己。
 *
 * ⚠️ **已知殘留**：玩家若同時在另一場**別的**進行中對局（例如另一個分頁排到隊），
 *    這支函式會回那一場的 id，於是再戰尾窗期間他會被帶過去。
 *    這與 `LobbyPage` 的「你有一場進行中的對局 → 回到對局」是同一個行為，
 *    而且那一場同樣受 30 秒 ready 逾時管轄（不去等於被判負），所以**不特別擋**。
 *    不用「新 id 一定比舊 id 大」來收窄，是因為那道 guard 一旦因為 id 生成方式改變而
 *    失效，症狀是**安靜地不再救援** —— 而安靜的失效正是這一整輪在修的東西。
 */
export function pickDiscoveredMatchId(myActiveMatch: unknown, currentMatchId: string | null): string | null {
  if (typeof myActiveMatch !== 'string') return null;
  if (myActiveMatch === '' || myActiveMatch === currentMatchId) return null;
  return myActiveMatch;
}

export function useMatch(matchId: string | null): UseMatchResult {
  const { api, socket, onMessage, connection, reconnectAttempt } = useApp();
  const [state, dispatch] = useReducer(matchReducer, matchId, initialState);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const roundTotalRef = useRef<number | null>(null);
  const lastDeadlineRef = useRef<string | null>(null);

  // --- 載入 / 重新載入 Observation（C02 §5） -------------------------------
  const refresh = useCallback(() => {
    if (!matchId) return;
    const ac = new AbortController();
    api
      .getMatch(matchId, ac.signal)
      .then((m) => {
        setLoadError(null);
        dispatch({ type: 'HYDRATE', match: m });
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setLoadError(e instanceof ApiError ? e.code : String(e));
      });
  }, [api, matchId]);

  useEffect(() => {
    if (!matchId) return;
    refresh();
  }, [matchId, refresh]);

  // --- WebSocket 訂閱 -----------------------------------------------------
  useEffect(() => {
    if (!matchId || !socket) return;
    socket.trackMatch(matchId);
    return () => socket.untrackMatch(matchId);
  }, [matchId, socket]);

  useEffect(() => {
    return onMessage((msg: WsServerMessage) => {
      if (msg.op === 'event') {
        // ★ 一條 WS 可以訂閱多個頻道，而 `untrackMatch()` 只清本地紀錄、**不會**送
        //   `unsubscribe` 給伺服器（見 api/ws.ts）—— 導向新對局之後，舊對局的事件
        //   仍會沿著同一條連線源源不絕地送過來。沒有這道 guard，殘留的舊訂閱會把
        //   玩家從新對局彈開（reducer 內部雖然也擋一次，但那道守衛在 matchId
        //   還沒進 state 時會短路）。
        if (matchId && msg.match_id !== matchId) return;
        dispatch({ type: 'SERVER_EVENT', event: msg });
        return;
      }
      if (msg.op === 'sync' && msg.match_id === matchId && msg.match) {
        dispatch({ type: 'HYDRATE', match: msg.match });
        return;
      }
      if (msg.op === 'error') {
        dispatch({ type: 'SUBMIT_FAILED', code: msg.code, message: msg.message });
      }
    });
  }, [onMessage, matchId]);

  // --- 連線狀態 ------------------------------------------------------------
  const prevConnection = useRef(connection);
  useEffect(() => {
    const phase =
      connection === 'open'
        ? 'online'
        : connection === 'reconnecting'
          ? 'reconnecting'
          : connection === 'closed'
            ? 'offline'
            : 'connecting';
    dispatch({ type: 'CONNECTION', state: phase, attempt: reconnectAttempt });

    // 重連成功後，除了 WS 的 resync（C02 §6.3）之外，再走一次 REST。
    // 理由：resync 回覆的 envelope 形狀契約沒寫死，REST 那條是明文保證的，
    // 兩條都走才不會卡在「畫面停在斷線前」。
    if (prevConnection.current !== 'open' && connection === 'open') refresh();
    prevConnection.current = connection;
  }, [connection, reconnectAttempt, refresh]);

  // machine 說需要重新同步（ROUND_MISMATCH 之類）→ 立刻補一次
  useEffect(() => {
    if (state.needsResync) refresh();
  }, [state.needsResync, refresh]);

  /*
   * ★ C02 §6.1 / §6.2：協定不相容時「**必須記錄錯誤**」。
   *
   * reducer 是純函式（machine.ts 檔頭把這件事當成它可測試性的基礎），所以它只把
   * 錯誤**記進 state**；真正的 log 由這一層做 —— 純度與契約兩邊都保住。
   * 同一個錯誤只記一次，否則每次重繪都會刷一行。
   *
   * ★ R-6 之後這裡記的是**每一個** `state.error`，不只協定錯誤。
   *   原因是畫面那一側從此只顯示翻譯過的句子（`errorNoticeKey()`），
   *   技術細節（`code` / `message`）如果不在這裡落地就會**完全消失** ——
   *   而那正是「玩家看不懂、開發者也查不到」的雙輸。
   *   兩條契約要求的錯誤仍然用它們自己的措辭，因為那是拿來對照契約條文的。
   */
  const loggedErrorRef = useRef<string | null>(null);
  useEffect(() => {
    const err = state.error;
    if (!err) return;
    const signature = `${state.matchId ?? '-'}|${err.code}|${err.message}`;
    if (loggedErrorRef.current === signature) return;
    loggedErrorRef.current = signature;
    const detail = { match_id: state.matchId, code: err.code, message: err.message };
    if (err.code === PROTOCOL_MISMATCH) {
      console.error('[elab-gaming] 協定不相容（C02 §6.1 / §6.2）', detail);
      return;
    }
    console.error('[elab-gaming] 對局錯誤（畫面只會顯示翻譯過的一句話）', detail);
  }, [state.error, state.matchId]);

  // --- 揭曉動畫 -----------------------------------------------------------
  useEffect(() => {
    if (state.phase !== 'revealing') return;
    const prefersReduced =
      typeof globalThis.matchMedia === 'function' && globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timer = setTimeout(() => dispatch({ type: 'REVEAL_DONE' }), prefersReduced ? 0 : REVEAL_MS);
    return () => clearTimeout(timer);
  }, [state.phase, state.lastResolved?.roundNo]);

  // --- 倒數 ---------------------------------------------------------------
  useEffect(() => {
    if (!state.deadlineAt) return;
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, [state.deadlineAt]);

  const remainingMs = useMemo(() => {
    if (!state.deadlineAt) return null;
    const deadline = Date.parse(state.deadlineAt);
    if (Number.isNaN(deadline)) return null;
    // 用伺服器校正過的時間，避免玩家的時鐘歪掉導致倒數不準（api.serverNow()）。
    void now; // 只是為了讓 memo 隨計時器更新
    return Math.max(0, deadline - api.serverNow());
  }, [state.deadlineAt, now, api]);

  useEffect(() => {
    if (state.deadlineAt !== lastDeadlineRef.current) {
      lastDeadlineRef.current = state.deadlineAt;
      roundTotalRef.current = remainingMs && remainingMs > 0 ? remainingMs : null;
    }
  }, [state.deadlineAt, remainingMs]);

  useEffect(() => {
    if (remainingMs === 0 && !state.deadlinePassed) dispatch({ type: 'DEADLINE_PASSED' });
  }, [remainingMs, state.deadlinePassed]);

  const progress = useMemo(() => {
    const total = roundTotalRef.current;
    if (!total || remainingMs === null) return 0;
    return Math.min(1, Math.max(0, remainingMs / total));
  }, [remainingMs]);

  // --- 出手 ---------------------------------------------------------------
  const submit = useCallback(
    (action: ActionKey) => {
      if (!matchId || !state.matchId) return;
      const key = randomIdempotencyKey();
      const roundNo = state.roundNo;
      dispatch({ type: 'SUBMIT_LOCAL', action, idempotencyKey: key });

      // 主通道走 REST：POST /matches/{id}/actions 是契約裡明確帶 Idempotency-Key 的那條，
      // 網路重試不會造成重複出手（C02 §0/§5）。WS 的 action op 留給未來的低延遲路徑。
      api
        .submitAction(matchId, { round_no: roundNo, action }, key)
        .then((resp) => {
          // ★ 刻意忽略 resp.waiting_for —— 見 machine.ts 檔頭。
          dispatch({ type: 'SUBMIT_ACK', roundNo: resp.round_no ?? roundNo });
        })
        .catch((e: unknown) => {
          const code = e instanceof ApiError ? e.code : 'NETWORK_ERROR';
          const message = e instanceof Error ? e.message : String(e);
          dispatch({ type: 'SUBMIT_FAILED', code, message });
        });
    },
    [api, matchId, state.matchId, state.roundNo],
  );

  /**
   * ★ **回傳結果**，不要丟掉它。
   *   後端回 `{status, match_id}`：`matched` 代表對方也按了、**新的對局已經開好**，
   *   `match_id` 是新的那一場。舊版是 `await api.rematch(matchId)` 然後把結果丟掉 ——
   *   於是雙方都按了再戰，卻沒有人會被帶進新的對局，畫面停在「等待對手」。
   *
   * ★ **先按的人拿到的是 `waiting_for_opponent`，而且不會再有第二個 HTTP 回應。**
   *   他的**主要**通知管道是 WS 的 `rematch_ready`（C02 §5 v1.2）→ `state.rematchMatchId`。
   *
   * ★ **重打這支端點是安全的，而且刻意被設計成安全的。**
   *   （這段註解上一版寫的是相反的話 —— 它描述的是**第一輪修復之前**的後端。
   *     現在的 `routes_matches.py` 是這樣的：）
   *
   *   ```python
   *   settled = _as_text(await container.redis.get(key_new))   # ← 早退在……
   *   if settled:
   *       return {"status": "matched", "match_id": settled}
   *   await container.redis.set(key_mine, "1", ex=REMATCH_TTL_S)  # ← ……寫入之前
   *   ```
   *
   *   * 再戰已經成立 ⇒ 早退發生在寫入任何東西**之前**，重打是一次**零副作用的讀取**，
   *     而且永遠回同一個新對局 id（收斂）。
   *   * 還沒成立 ⇒ 重打只是把**自己**的意願續期（`SET ... EX 120`），
   *     既不消耗對方的 key，也不會多開對局 —— 唯一的 id 由 `SET NX` 選舉決定。
   *
   *   後端註解與 `tests/api/test_rematch_notification.py::
   *   test_rematch_is_idempotent_and_publishes_exactly_once` 都明文把
   *   「前端輪詢 `/rematch` 直到拿到 `matched`」列為**受支持的補救路徑**。
   *
   *   為什麼需要那條補救路徑：`rematch_ready` 會掉 —— publish 失敗時後端**仍然回 200**
   *   只記 `log.error`；WS 正在退避重連（最長 15 秒）時 pub/sub 沒有回溯。
   *   掉一次而沒有補救，玩家看到的就是 FINDINGS F-3 的原始畫面。
   *   等待的節奏與逾時由 `MatchPage` 那一層決定（見 `rematchUiReducer`）。
   */
  const rematch = useCallback(async (): Promise<RematchResponse> => {
    if (!matchId) throw new Error('no match to rematch');
    return api.rematch(matchId);
  }, [api, matchId]);

  /**
   * ★ 再戰尾窗的唯讀守望（見 `UseMatchResult.findMyActiveMatch` 的說明）。
   *
   * 三個刻意的細節：
   *  * **只在再戰等待期間被呼叫**（`MatchPage` 的 `shouldWatchNow()`）。
   *    這一層不自己排程 —— 否則就變成「對局頁固定輪詢大廳」，那是另一回事。
   *  * **過濾掉目前這場**。玩家正坐在一場已結束的對局上，`my_active_match`
   *    不會是它（status 不在 waiting/ready/in_progress）；但萬一後端某天改了
   *    語意，這道 guard 讓最壞情況是「什麼都沒發生」，而不是把玩家導向自己。
   *  * **失敗往上丟**，由呼叫端決定要不要在意。這裡不吞例外，
   *    因為「守望一直失敗」是值得在 console 看到的事。
   */
  const findMyActiveMatch = useCallback(async (): Promise<string | null> => {
    const lobby = await api.getLobby(config.gameKey);
    return pickDiscoveredMatchId(lobby.my_active_match, matchId);
  }, [api, matchId]);

  const forfeit = useCallback(async () => {
    if (!matchId) return;
    await api.forfeit(matchId);
  }, [api, matchId]);

  return {
    state,
    rematchMatchId: readRematchMatchId(state),
    remainingMs,
    progress,
    submit,
    rematch,
    findMyActiveMatch,
    forfeit,
    refresh,
    loadError,
  };
}
